import { MonetaryAmount, Shimmer } from '@components'
import { useBusinessContext } from '@context'
import { IRelatedDocumentRole, IRelationType } from '@query/documentRelations'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import styled, { useTheme } from 'styled-components'

import {
  MismatchFooterContent,
  MismatchFooterBody,
  MismatchFooterTitle,
  RelationGroupFooter,
  RelationGroupFooterStatus,
  RelationLead,
  RelationLeadRow,
  RelationListWrapper,
  RelationListWrapperMismatch,
  RelationMapperThemeMode
} from './RelationMapper.styled'
import { createRelationKey, DocumentRelationFormValue, RelationListItem } from './RelationRow'
import {
  getAccrualLeadState,
  getLeadSentenceKey,
  getRelationFooterAmounts,
  getSettlementStatus,
  sumRelationAmounts
} from './relationMapperUtils'
import { useRelatedDocumentsByRelation } from './useRelationLeadContent'

export const RelationGroup: React.FC<{
  relations: DocumentRelationFormValue[]
  onRemove: (relation: DocumentRelationFormValue) => void
  readOnly?: boolean
  themeMode: RelationMapperThemeMode
  sourceAmount: number | null
  openAmount: number | null
  hasMismatch: boolean
  networkInvoiceTotal?: number | null
  networkPaymentTotal?: number | null
  isFooterLoading?: boolean
  disableRelationPreview?: boolean
  hideLead?: boolean
}> = ({
  relations,
  onRemove,
  readOnly = false,
  themeMode,
  sourceAmount,
  openAmount,
  hasMismatch,
  networkInvoiceTotal = null,
  networkPaymentTotal = null,
  isFooterLoading = false,
  disableRelationPreview = false,
  hideLead = false
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { slug } = useBusinessContext()
  const currentRole = relations[0]?.role

  const { relatedDocumentsById, isResolvingAmounts } = useRelatedDocumentsByRelation(
    relations,
    slug
  )

  if (!currentRole || relations.length === 0) {
    return null
  }

  const linkedTotal = sumRelationAmounts(relations, relatedDocumentsById)
  const isSettlementRole = currentRole === IRelatedDocumentRole.SETTLEMENT
  const hasNetworkTotals = networkInvoiceTotal != null && networkPaymentTotal != null
  const { appliedAmount, targetAmount, liveOpenAmount } = getRelationFooterAmounts({
    role: currentRole,
    sourceAmount,
    linkedTotal,
    fallbackOpenAmount: openAmount,
    networkInvoiceTotal,
    networkPaymentTotal
  })
  const status = getSettlementStatus({
    invoiceAmount: targetAmount,
    paidAmount: appliedAmount,
    openAmount: liveOpenAmount,
    hasMismatch
  })
  const showFooterLoading = isFooterLoading || (isResolvingAmounts && !hasNetworkTotals)
  const Wrapper = hasMismatch ? RelationListWrapperMismatch : RelationListWrapper
  const amountLabelKey = isSettlementRole
    ? 'documentRelations.status.clearedLabel'
    : 'documentRelations.status.paidLabel'
  const isSalaryPayment = relations[0].type === IRelationType.SALARY_PAYMENT
  const mismatchPaymentsPrefixKey = isSalaryPayment
    ? 'documentRelations.status.mismatchSalaryPaymentsTotalPrefix'
    : 'documentRelations.status.mismatchPaymentsTotalPrefix'
  const mismatchInvoicesPrefixKey = isSalaryPayment
    ? 'documentRelations.status.mismatchSalaryAccrualsTotalPrefix'
    : 'documentRelations.status.mismatchInvoicesTotalPrefix'
  const mismatchAmount = Math.abs((targetAmount ?? 0) - appliedAmount)
  const isMismatchOverpaid = targetAmount != null && appliedAmount > targetAmount
  const mismatchLabelKey = isMismatchOverpaid
    ? 'documentRelations.status.mismatchOverpaidLabel'
    : 'documentRelations.status.mismatchOpenLabel'
  const isSettled = status === 'settled'
  const isPartialStatus = status === 'partial'
  const footerEmphasis = hasMismatch || isPartialStatus
  const footerStatusVariant = isPartialStatus ? 'warning' : 'success'
  const shimmerBackground =
    themeMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : theme.colors.neutralWhite
  const showFooter = showFooterLoading || status !== 'none'

  return (
    <>
      {hideLead ? null : (
        <RelationLeadRow>
          <RelationLead $themeMode={themeMode}>
            <Trans
              i18nKey={getLeadSentenceKey(currentRole, {
                leadState: isSettlementRole
                  ? 'full'
                  : getAccrualLeadState({
                      status: showFooterLoading ? 'none' : status,
                      appliedAmount,
                      targetAmount
                    }),
                relationType: relations[0].type
              })}
            />
          </RelationLead>
        </RelationLeadRow>
      )}
      <Wrapper $themeMode={themeMode}>
        {relations.map(relation => (
          <RelationListItem
            key={createRelationKey(relation)}
            relation={relation}
            onRemove={onRemove}
            readOnly={readOnly}
            themeMode={themeMode}
            disableRelationPreview={disableRelationPreview}
          />
        ))}
        {showFooter ? (
          <RelationGroupFooter
            $themeMode={themeMode}
            $warning={footerEmphasis && !showFooterLoading}
          >
            {showFooterLoading ? (
              <FooterShimmerColumn>
                <Shimmer height="0.75rem" width="55%" backgroundColor={shimmerBackground} />
                <Shimmer height="0.65rem" width="85%" backgroundColor={shimmerBackground} />
              </FooterShimmerColumn>
            ) : hasMismatch ? (
              <MismatchFooterContent>
                <MismatchFooterTitle>
                  {t(mismatchLabelKey)} <MonetaryAmount value={mismatchAmount} />.
                </MismatchFooterTitle>
                <MismatchFooterBody>
                  {t(mismatchPaymentsPrefixKey)} <MonetaryAmount value={appliedAmount} />{' '}
                  {t(mismatchInvoicesPrefixKey)}{' '}
                  <MonetaryAmount value={targetAmount ?? appliedAmount} />
                  {t('documentRelations.status.mismatchGuidance')}
                </MismatchFooterBody>
              </MismatchFooterContent>
            ) : (
              <>
                <span>
                  <MonetaryAmount value={appliedAmount} /> {t('documentRelations.status.of')}{' '}
                  <MonetaryAmount value={targetAmount ?? appliedAmount} /> {t(amountLabelKey)}
                </span>
                <RelationGroupFooterStatus
                  $themeMode={themeMode}
                  $variant={footerStatusVariant}
                  $onEmphasizedFooter={footerEmphasis}
                >
                  {isSettled ? <FaCheck aria-hidden /> : null}
                  {isSettled
                    ? t('documentRelations.status.fullySettled')
                    : t('documentRelations.status.partiallyOpen')}
                </RelationGroupFooterStatus>
              </>
            )}
          </RelationGroupFooter>
        ) : null}
      </Wrapper>
    </>
  )
}

const FooterShimmerColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xxs}rem 0;
`
